import React, {useEffect, useState} from 'react';
import {useTranslation} from 'react-i18next';
import workshopsService from '../services/workshopsService';
import {Workshop} from '../types/Workshop';
import {DataTable, DataTableFilterMeta} from 'primereact/datatable';
import {Column} from 'primereact/column';
import {Button} from 'primereact/button';
import {useNavigate} from "react-router-dom";
import AdminFormHeader from "../../../layout/admin/AdminFormHeader";
import {AdminButton} from "../../../layout/admin";
import {FilterMatchMode} from "primereact/api";
import {handleError} from "../../../common/utils/errorHandling";
import {useToast} from "../../../common/contexts/ToastContext";
import {useAdminConfirmDialog} from "../../../common/hooks/useAdminConfirmDialog";

const WorkshopsList: React.FC = () => {
  const {t} = useTranslation();
  const {showError, showSuccess} = useToast();
  const {showConfirmDialog} = useAdminConfirmDialog();
  const [workshops, setWorkshops] = useState<Workshop[]>([]);
  const navigate = useNavigate();
  const [filters, setFilters] = useState<DataTableFilterMeta>({
    name: {value: null, matchMode: FilterMatchMode.CONTAINS},
    location: {value: null, matchMode: FilterMatchMode.CONTAINS}
  });

  useEffect(() => {
    fetchWorkshops();
  }, []);

  const fetchWorkshops = async (): Promise<void> => {
    const workshopsData = await workshopsService.getAllWorkshops();
    setWorkshops(workshopsData);
  };

  const handleDelete = async (workshop: Workshop): Promise<void> => {
    showConfirmDialog({
      message: t('admin.common.messages.confirmDeleteWithName', {name: workshop.name}),
      accept: async () => {
        await handleError(
          async () => {
            await workshopsService.deleteWorkshop(workshop.id);
            await fetchWorkshops();
          },
          t('admin.workshops.errors.deleting'),
          undefined,
          showError
        );
      }
    });
  };

  const handleEdit = (workshop: Workshop) => {
    navigate(`${workshop.id}/edit`);
  };

  const handleCreateNew = () => {
    navigate('new');
  };

  return (
    <>
      <AdminFormHeader
        title={t('admin.workshops.title.list')}
        subtitle={t('admin.workshops.subtitle.list')}
      />

      <AdminButton
        onClick={handleCreateNew}
        variant="black"
        label={t('admin.workshops.actions.createNew')}
      />

      <DataTable
        value={workshops}
        stripedRows
        paginator
        rows={10}
        filterDisplay="row"
        filters={filters}
        columnResizeMode="fit"
        resizableColumns
        showGridlines
      >
        <Column
          sortField="name"
          header={t('admin.common.columns.name')}
          sortable
          filter
          filterField="name"
          filterPlaceholder={t('admin.common.filters.searchByName')}
          showFilterMenu={false}
          showClearButton={false}
          body={(workshop: Workshop) => (
            <strong>{workshop.name}</strong>
          )}/>
        <Column
          sortField="location"
          header={t('admin.workshops.columns.location')}
          sortable
          filter
          filterField="location"
          filterPlaceholder={t('admin.workshops.placeholders.searchByLocation')}
          showFilterMenu={false}
          showClearButton={false}
          body={(workshop: Workshop) => (
            <span>{workshop.location}</span>
          )}/>
        <Column header={t('admin.common.columns.actions')} body={(workshop: Workshop) => (
          <div>
            <Button
              onClick={() => handleEdit(workshop)}
              icon="fa fa-edit"
              severity="info"
              size="small"
              outlined
              className="mr-2"
              aria-label={t('admin.common.actions.edit')}
              tooltip={t('admin.common.actions.edit')}
              tooltipOptions={{position: 'top'}}
            />
            <Button
              onClick={() => handleDelete(workshop)}
              icon="fa fa-trash"
              severity="danger"
              size="small"
              outlined
              aria-label={t('admin.common.actions.delete')}
              tooltip={t('admin.common.actions.delete')}
              tooltipOptions={{position: 'top'}}
            />
          </div>)}/>
      </DataTable>
    </>
  );
};

export default WorkshopsList;

